class StickyCartPanel extends HTMLElement {
  constructor() {
    super();
    this.trigger = this.querySelector('[data-sticky-action]');
    this.openTriggers = this.querySelectorAll('[data-sticky-open]');
    this.closeTriggers = this.querySelectorAll('[data-sticky-close]');
    this.sheet = this.querySelector('[data-sticky-sheet]');
    this.form = this.querySelector('form');
    this.variantInput = this.querySelector('[data-sticky-variant-id]');
    this.optionGroups = Array.from(this.querySelectorAll('[data-sticky-option-group]'));
    this.optionInputs = Array.from(this.querySelectorAll('[data-sticky-option]'));
    this.variants = JSON.parse(this.querySelector('[data-sticky-variants]').textContent);
    this.submitButton = this.querySelector('.sticky-cart-sheet__submit');
    this.quantityInput = this.querySelector('input[name="quantity"]');
    this.productState = this.querySelector('[data-sticky-product-state]');
    this.cartState = this.querySelector('[data-sticky-cart-state]');
    this.cartOpenTriggers = this.querySelectorAll('[data-sticky-cart-open]');
    this.cartImage = this.querySelector('[data-sticky-cart-image]');
    this.cartProduct = this.querySelector('[data-sticky-cart-product]');
    this.cartVariant = this.querySelector('[data-sticky-cart-variant]');
    this.cartTotal = this.querySelector('[data-sticky-cart-total]');
    this.checkoutButton = this.querySelector('[data-sticky-checkout]');
    this.checkoutLabel = this.querySelector('[data-sticky-checkout-label]');
    this.requiresExplicitSelection = this.optionGroups.length > 0;
    this.hasExplicitSelection = !this.requiresExplicitSelection;

    if (this.requiresExplicitSelection) {
      this.optionInputs.forEach((input) => {
        input.checked = false;
      });
      this.variantInput.value = '';
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.querySelector('.sticky-cart').classList.add('sticky-cart__select_options__text');
    }

    window.addEventListener('scroll', this.update.bind(this));
    this.update();
    this.trigger.addEventListener('click', this.onClickHandler.bind(this));
    this.openTriggers.forEach((button) => button.addEventListener('click', this.openSheet.bind(this)));
    this.closeTriggers.forEach((button) => button.addEventListener('click', this.closeSheet.bind(this)));
    this.optionInputs.forEach((input) => input.addEventListener('change', this.onOptionChange.bind(this)));
    this.querySelectorAll('[data-sticky-quantity-change]').forEach((button) => {
      button.addEventListener('click', this.onQuantityChange.bind(this));
    });
    this.cartOpenTriggers.forEach((trigger) => trigger.addEventListener('click', this.openCartDetails.bind(this)));
    document.addEventListener('keydown', this.onKeydown.bind(this));

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshCartState());
    }
    this.refreshCartState();

  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  update() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const section = this.closest('.shopify-section') || this.closest('.popup-modal__content__data');
    if (!button || !section) return;

    const addButton = section.querySelector('select-option-js')
      ? button.querySelector('.select-options-button')
      : button.querySelector('[name="add"]');
    if (!addButton) return;

    const rangeToShowModal = this.getTop(addButton) + addButton.clientHeight;
    if (rangeToShowModal <= 0) return;

    if (window.scrollY >= rangeToShowModal && !this.classList.contains('show-modal')) {
      this.classList.add('show-modal');
      document.body.classList.add('modal__sticky-cart');
      const footerContent = document.querySelector('.footer__content');
      if (footerContent) footerContent.classList.add('sticky-cart-modal_bottom_padding');
    }

    if (window.scrollY < rangeToShowModal && this.classList.contains('show-modal') && !this.classList.contains('is-open')) {
      this.classList.remove('show-modal');
      document.body.classList.remove('modal__sticky-cart');
      const footerContent = document.querySelector('.footer__content');
      if (footerContent) footerContent.classList.remove('sticky-cart-modal_bottom_padding');
    }
  }

  onClickHandler() {
    if (!this.hasExplicitSelection || !this.variantInput.value) {
      this.openSheet();
      return;
    }

    this.form.requestSubmit();
  }

  openSheet() {
    if (!this.sheet) return;
    this.sheet.hidden = false;
    this.classList.add('is-open');
    this.openTriggers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      const emptyGroup = this.optionGroups.find((group) => !group.querySelector('[data-sticky-option]:checked'));
      const target = emptyGroup?.querySelector('[data-sticky-option]:not(:disabled)') || this.querySelector('.sticky-cart-sheet__close');
      if (target) target.focus();
    });
  }

  closeSheet(restoreFocus = true) {
    if (!this.sheet) return;
    this.classList.remove('is-open');
    this.openTriggers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
    document.body.style.overflow = '';
    window.setTimeout(() => {
      if (!this.classList.contains('is-open')) this.sheet.hidden = true;
    }, 250);
    if (restoreFocus) this.trigger.focus();
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.classList.contains('is-open')) this.closeSheet();
  }

  onOptionChange() {
    const selectedOptions = this.optionGroups.map((group) => {
      return group.querySelector('[data-sticky-option]:checked')?.value || '';
    });
    const allSelected = selectedOptions.every(Boolean);
    const variant = allSelected
      ? this.variants.find((item) => item.options.every((option, index) => option === selectedOptions[index]))
      : null;

    this.updateVariant(variant);
  }

  onQuantityChange(event) {
    const change = Number(event.currentTarget.dataset.stickyQuantityChange);
    const current = Math.max(1, Number(this.quantityInput.value) || 1);
    this.quantityInput.value = Math.max(1, current + change);
    this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  openCartDetails(event) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      event.preventDefault();
      cartDrawer.open(event.currentTarget);
      return;
    }

    if (event.currentTarget.tagName === 'BUTTON') {
      window.location.href = this.dataset.cartUrl;
    }
  }

  async refreshCartState() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      this.renderCartState(await response.json());
    } catch (error) {
      console.error(error);
    }
  }

  renderCartState(cart) {
    if (!cart || !cart.item_count || !cart.items?.length) {
      this.productState.hidden = false;
      this.cartState.hidden = true;
      return;
    }

    const item = cart.items.find((cartItem) => String(cartItem.product_id) === this.dataset.productId);
    if (!item) {
      this.productState.hidden = false;
      this.cartState.hidden = true;
      return;
    }

    const currency = cart.currency || this.dataset.currency;
    const total = this.formatMoney(cart.total_price, currency);
    const cartTitle = this.dataset.cartTitleTemplate.replace('__COUNT__', cart.item_count);
    const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';

    this.cartProduct.textContent = item.product_title || item.title || cartTitle;
    this.cartVariant.textContent = [variantTitle, `× ${item.quantity}`].filter(Boolean).join(' · ');
    this.cartTotal.textContent = `${cartTitle} · ${this.dataset.cartTotalLabel} ${total}`;
    this.checkoutLabel.textContent = this.dataset.checkoutLabel;
    this.checkoutButton.setAttribute('aria-label', this.dataset.checkoutTemplate.replace('__PRICE__', total));

    if (item.image) {
      this.cartImage.src = item.image;
      this.cartImage.alt = item.product_title || item.title || '';
      this.cartImage.hidden = false;
    } else {
      this.cartImage.removeAttribute('src');
      this.cartImage.alt = '';
      this.cartImage.hidden = true;
    }

    this.productState.hidden = true;
    this.cartState.hidden = false;
    if (this.classList.contains('is-open')) this.closeSheet(false);
  }

  updateVariant(variant) {
    const available = Boolean(variant && variant.available);
    this.variantInput.value = variant ? variant.id : '';
    this.submitButton.disabled = !available;
    this.submitButton.setAttribute('aria-disabled', String(!available));

    if (!variant) {
      this.hasExplicitSelection = false;
      this.querySelector('.sticky-cart').classList.add('sticky-cart__select_options__text');
      return;
    }

    this.hasExplicitSelection = true;
    this.querySelector('.sticky-cart').classList.remove('sticky-cart__select_options__text');
    this.querySelectorAll('[data-sticky-price]').forEach((price) => {
      price.textContent = this.formatMoney(variant.price);
    });

    if (variant.featured_image && variant.featured_image.src) {
      this.querySelectorAll('.sticky-cart__image, .sticky-cart-sheet__image').forEach((image) => {
        image.src = variant.featured_image.src;
        image.removeAttribute('srcset');
      });
    }
  }

  formatMoney(cents, currency = this.dataset.currency) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, this.dataset.moneyFormat);
    }

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  }

  getTop(element) {
    return element.offsetTop + (element.offsetParent && this.getTop(element.offsetParent));
  }
}

if (!customElements.get('sticky-cart-panel')) {
  customElements.define('sticky-cart-panel', StickyCartPanel);
}
